import React, { useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { SelectAutocomplete } from "components/forms";
import { SimpleHeadMui } from "components";
import dayjs from "dayjs";
import toIsoMidday from "utils/to-iso-midday";

import { defaultWideFilters, WideFilters } from "store/types";

interface Props {
  open: boolean;
  onClose: () => void;
  filters: WideFilters;
  onApply: (f: WideFilters) => void;
  investorOptions: { id: string; name: string }[];
  roundOptions: { id: string; name: string }[];
  measureOptions: string[];
}

export const WideFiltersDrawer: React.FC<Props> = ({
  open,
  onClose,
  filters,
  onApply,
  investorOptions,
  roundOptions,
  measureOptions
}) => {
  const [local, setLocal] = useState<WideFilters>(filters);

  useEffect(() => {
    if (open) setLocal(filters);
  }, [open]);

  const setDate = (field: "dateFrom" | "dateTo", value: string | null) =>
    setLocal((prev) => ({ ...prev, [field]: value }));

  const allMeasuresVisible = local.visibleMeasures.length === 0;

  const toggleMeasure = (name: string) =>
    setLocal((prev) => {
      const already = prev.visibleMeasures.includes(name);
      return {
        ...prev,
        visibleMeasures: already
          ? prev.visibleMeasures.filter((m) => m !== name)
          : [...prev.visibleMeasures, name]
      };
    });

  const toggleAllMeasures = () =>
    setLocal((prev) => ({ ...prev, visibleMeasures: allMeasuresVisible ? [...measureOptions] : [] }));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{ ".MuiPaper-root": { borderRadius: "0 20px 20px 0", minWidth: 430, maxWidth: 430 } }}
    >
      <form
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
        onSubmit={(e) => {
          e.preventDefault();
          onApply(local);
          onClose();
        }}
      >
        <SimpleHeadMui onIconClick={onClose}>Filter and Sort</SimpleHeadMui>

        <Stack sx={{ flexGrow: 1, overflowY: "auto", gap: "24px", p: "18px 30px" }}>
          {/* Date range */}
          <Typography sx={{ fontSize: "15px", color: "#0a3142", borderBottom: "1px solid #dae0e2", pb: "5px" }}>
            Date Range
          </Typography>
          <Stack direction="row" gap="12px" alignItems="center">
            <DesktopDatePicker
              label="From"
              value={local.dateFrom ? dayjs(local.dateFrom) : null}
              onChange={(d) => { if (d && d.isValid()) setDate("dateFrom", toIsoMidday(d)); else setDate("dateFrom", null); }}
            />
            <Typography>–</Typography>
            <DesktopDatePicker
              label="To"
              value={local.dateTo ? dayjs(local.dateTo) : null}
              onChange={(d) => { if (d && d.isValid()) setDate("dateTo", toIsoMidday(d)); else setDate("dateTo", null); }}
            />
          </Stack>
          <Stack direction="row" gap={1}>
            {local.dateFrom && (
              <Chip
                label={`From: ${local.dateFrom.slice(0, 10)}`}
                size="small"
                onDelete={() => setDate("dateFrom", null)}
              />
            )}
            {local.dateTo && (
              <Chip
                label={`To: ${local.dateTo.slice(0, 10)}`}
                size="small"
                onDelete={() => setDate("dateTo", null)}
              />
            )}
          </Stack>

          {/* Investors */}
          <SelectAutocomplete<{ id: string; name: string }, true>
            options={investorOptions}
            value={local.investors}
            multiple
            selectAll
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(o) => o.name}
            compareParam={(o) => o.id}
            setValue={(opts) => setLocal((prev) => ({ ...prev, investors: opts }))}
            renderInput={(params) => <TextField {...params} label="Investors" />}
            disableCloseOnSelect
          />

          {/* Rounds */}
          <SelectAutocomplete<{ id: string; name: string }, true>
            options={roundOptions}
            value={local.rounds}
            multiple
            selectAll
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            getOptionLabel={(o) => o.name}
            compareParam={(o) => o.id}
            setValue={(opts) => setLocal((prev) => ({ ...prev, rounds: opts }))}
            renderInput={(params) => <TextField {...params} label="Rounds" />}
            disableCloseOnSelect
          />

          {/* Columns (measures) */}
          {measureOptions.length > 0 && (
            <>
              <Typography
                sx={{ fontSize: "15px", color: "#0a3142", borderBottom: "1px solid #dae0e2", pb: "5px" }}
              >
                Columns
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allMeasuresVisible}
                      indeterminate={!allMeasuresVisible && local.visibleMeasures.length < measureOptions.length}
                      onChange={toggleAllMeasures}
                    />
                  }
                  label={<Typography fontWeight={500}>All columns</Typography>}
                />
                {measureOptions.map((m) => (
                  <FormControlLabel
                    key={m}
                    control={
                      <Checkbox
                        checked={allMeasuresVisible || !local.visibleMeasures.includes(m)}
                        onChange={() => toggleMeasure(m)}
                      />
                    }
                    label={m}
                  />
                ))}
              </FormGroup>
            </>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ boxShadow: "0 -1px 5px 1px rgba(0,0,0,.08)", p: "18px 30px" }}
        >
          <Typography
            sx={{ fontSize: "16px", textDecoration: "underline", fontWeight: 500, cursor: "pointer" }}
            onClick={() => setLocal(defaultWideFilters)}
          >
            Clear All Filters
          </Typography>
          <Stack direction="row" gap="18px">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Apply
            </Button>
          </Stack>
        </Stack>
      </form>
    </Drawer>
  );
};
